import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import SkeletonBox from '@/components/atoms/SkeletonBox/SkeletonBox';
import Tooltip from '@/components/atoms/Tooltip';
import CircularChart, { type CircularSlice } from '@/components/charts/CircularChart';
import { ChartKeySwatch } from '@/components/charts/legend';
import { useChartTheme } from '@/components/charts/theme';
import { formatWallClockWorded } from '@/utils/date';
import { formatPercentageByLocale, parseLocale } from '@/utils/localeFormatters';

import styles from './RespirationPercentagesCard.module.scss';

interface RespirationPercentagesCardProps {
    isLoading?: boolean;
    passive?: number;
    active?: number;
    /** The site's wall clock (`YYYY-MM-DDTHH:mm:ss`, no zone), shown as it is. */
    lastMeasurement: string;
}

export default function RespirationPercentagesCard(props: RespirationPercentagesCardProps) {
    const { isLoading = false, passive, active, lastMeasurement } = props;

    const { t, i18n } = useTranslation('behaviorModule');
    const locale = parseLocale(i18n.language);

    // Read through the chart theme, which follows a theme switch; a one-off getComputedStyle
    // call kept the colours of whichever theme the card first rendered in.
    const theme = useChartTheme();
    const colors = useMemo(
        () => ({
            passiveColor: theme.resolve('accent-ocean-green-400'),
            activeColor: theme.resolve('accent-orange-400'),
        }),
        [theme]
    );

    const slices: CircularSlice[] = useMemo(
        () => [
            {
                label: t('overview.passiveRespiration'),
                value: passive ?? 0,
                color: colors.passiveColor,
            },
            {
                label: t('overview.activeRespiration'),
                value: active ?? 0,
                color: colors.activeColor,
            },
        ],
        [passive, active, t, colors]
    );

    const tooltip = useMemo(
        () => ({
            title: (slice: CircularSlice) => slice.label,
            label: (slice: CircularSlice) => ` ${formatPercentageByLocale(slice.value, locale)}`,
        }),
        [locale]
    );

    return (
        <div className={styles.respirationPercentagesCardContainer}>
            <div className={styles.respirationPercentagesTitle}>{t('common.respiration')}</div>
            <div className={styles.respirationPercentagesSubtitle}>
                <span>{t('common.lastMeasurement')}</span>
                {isLoading ? (
                    <SkeletonBox width={150} height={24} borderRadius={8} />
                ) : (
                    <span>{formatWallClockWorded(lastMeasurement)}</span>
                )}
            </div>
            <div className={styles.respirationPercentagesContent}>
                <div className={styles.chartContainer}>
                    {isLoading ? (
                        <SkeletonBox width={124} height={124} borderRadius={8} />
                    ) : (
                        <CircularChart
                            kind="pie"
                            slices={slices}
                            tooltip={tooltip}
                            title={t('common.respiration')}
                        />
                    )}
                </div>
                <div className={styles.legendContainer}>
                    <Tooltip content={t('tooltips.passiveRespiration')}>
                        <LegendItem
                            color={colors.passiveColor}
                            label={t('overview.passiveRespiration')}
                            value={formatPercentageByLocale(passive, locale)}
                            isLoading={isLoading}
                        />
                    </Tooltip>
                    <Tooltip content={t('tooltips.activeRespiration')}>
                        <LegendItem
                            color={colors.activeColor}
                            label={t('overview.activeRespiration')}
                            value={formatPercentageByLocale(active, locale)}
                            isLoading={isLoading}
                        />
                    </Tooltip>
                </div>
            </div>
        </div>
    );
}

interface LegendItemProps {
    color: string;
    label: string;
    value: string;
    isLoading?: boolean;
}

function LegendItem(props: LegendItemProps) {
    const { color, label, value, isLoading = false } = props;

    return (
        <div className={styles.legendItem}>
            <ChartKeySwatch shape="dot" color={color} />
            <div className={styles.legendLabel}>{label}</div>
            <div className={styles.legendValue}>
                {isLoading ? (
                    <SkeletonBox width={100} height={26} borderRadius={8} />
                ) : (
                    <span>{value}</span>
                )}
            </div>
        </div>
    );
}
